/**
 * "Vendas pelo comercial": o que as vendedoras fecharam depois de um toque
 * da regua (ADR-001 E5, 25/09/2026).
 *
 * Decisoes nao obvias:
 *  1. O fluxo "toque -> resposta -> vendedora fechou -> credito do toque"
 *     fica DESENHADO no topo, em quatro passos: e a frase que o dono vai
 *     repetir para a empresa que perguntar "por que pago comissao se foi a
 *     minha vendedora que vendeu?". Texto curto, com o porque.
 *  2. Valor, quantidade e comissao sao so de conversoes ATIVAS, como o resto
 *     da pagina; uma venda do comercial revertida por chargeback aparece no
 *     bloco de estornos, nao aqui.
 *  3. Webhook x venda assistida lado a lado, cada um com atalho para o
 *     extrato filtrado: venda assistida e a que pede conferencia (referencia
 *     do pedido digitada por uma pessoa).
 *  4. O ranking de vendedoras usa `shortenName` e diz de qual empresa cada
 *     uma e; "Abrir comercial" troca a empresa em foco e leva a tela
 *     Comercial daquela empresa.
 *  5. Empresa sem vendedora cadastrada simplesmente nao tem essa linha: o
 *     vazio explica isso em vez de mostrar zero sem contexto.
 */

import { Badge, Button, CompanyLogo, Footnote, Icon, SectionCard, type IconName } from '../../../components/ui';
import { formatBRLFromCents, formatCount, formatPercentFromBps, shortenName } from '../../../lib/format';
import type { CompanySummary } from '../../../types';
import { participacaoBps } from '../comparar';
import type { ClosedByFilter, ComercialTotals, PeriodTotals } from '../types';
import { ENTRADA, plural } from '../vocabulario';
import { Delta } from './Delta';
import { identidadeDaEmpresa } from './IdentidadeEmpresa';

const MAXIMO_DE_VENDEDORAS = 5;

const PASSOS: readonly { readonly icone: IconName; readonly titulo: string; readonly texto: string }[] = [
  { icone: 'whatsapp', titulo: 'A régua tocou', texto: 'WhatsApp, ligação ou e-mail do motor' },
  { icone: 'resposta', titulo: 'O lead respondeu', texto: 'no número do comercial' },
  { icone: 'usuarios', titulo: 'A vendedora fechou', texto: 'pelo checkout ou venda assistida' },
  { icone: 'comissao', titulo: 'Comissão devida', texto: 'creditada ao último toque' },
];

export function VendasPeloComercial({
  comercial,
  previous,
  totalComissaoCents,
  companies,
  filtroAtivo,
  onVerNoExtrato,
  onAbrirComercial,
  className = '',
}: {
  readonly comercial: ComercialTotals;
  readonly previous: PeriodTotals | null;
  /** Comissao ativa do recorte, para a participacao. */
  readonly totalComissaoCents: number;
  readonly companies: readonly CompanySummary[];
  readonly filtroAtivo: ClosedByFilter;
  readonly onVerNoExtrato: (filtro: ClosedByFilter) => void;
  readonly onAbrirComercial: (companyId: string) => void;
  readonly className?: string;
}): JSX.Element {
  const peso = participacaoBps(comercial.commissionCents, totalComissaoCents);
  const vendedoras = comercial.sellers.slice(0, MAXIMO_DE_VENDEDORAS);
  const filtrando = filtroAtivo === 'comercial' || filtroAtivo === 'comercial_webhook' || filtroAtivo === 'venda_assistida';

  return (
    <SectionCard
      id="vendas-pelo-comercial"
      title="Vendas pelo comercial"
      icon="usuarios"
      description="A régua tocou, o lead respondeu e a vendedora fechou: a comissão é devida e fica creditada ao último toque do motor (ADR-001 E5). “Fechado por” é informação adicional, nunca substitui o contato creditado."
      aside={
        <Button variant={filtrando ? 'primary' : 'secondary'} size="sm" icon="filtro" onClick={() => onVerNoExtrato('comercial')} aria-pressed={filtroAtivo === 'comercial'}>
          {filtroAtivo === 'comercial' ? 'Filtrando o extrato' : 'Ver no extrato'}
        </Button>
      }
      className={`scroll-mt-20 ${className}`}
    >
      <div className="flex flex-col gap-5">
        <ol className="grid grid-cols-2 gap-2 sm:grid-cols-4" aria-label="Como a venda do comercial gera comissão">
          {PASSOS.map((passo, i) => (
            <li key={passo.titulo} className="relative flex items-start gap-2 rounded-xl border border-line bg-elevated px-3 py-2.5">
              <span aria-hidden="true" className={`grid h-7 w-7 shrink-0 place-items-center rounded-lg ${i === PASSOS.length - 1 ? 'bg-status-good/10 text-status-good' : 'bg-brand/10 text-brand'}`}>
                <Icon name={passo.icone} size="sm" />
              </span>
              <span className="min-w-0">
                <span className="block text-xs font-semibold text-ink">
                  <span className="sr-only">Passo {i + 1}: </span>
                  {passo.titulo}
                </span>
                <span className="block text-[11px] leading-snug text-ink-subtle">{passo.texto}</span>
              </span>
              {i < PASSOS.length - 1 ? (
                <span aria-hidden="true" className="absolute -right-2 top-1/2 z-10 hidden -translate-y-1/2 text-ink-subtle sm:block">
                  <Icon name="seta-dir" size="xs" />
                </span>
              ) : null}
            </li>
          ))}
        </ol>

        {comercial.count === 0 ? (
          <div className="rounded-xl border border-dashed border-edge bg-elevated px-4 py-6 text-center">
            <p className="text-sm font-medium text-ink">Nenhuma venda fechada pelo comercial no período.</p>
            <p className="mt-1 text-sm text-ink-muted">
              Quando o lead responder a um toque da régua e a vendedora fechar, a venda entra aqui com a comissão creditada ao toque. Empresas sem vendedora
              cadastrada (papel comercial) não têm essa linha; convide a equipe em Configurações → Equipe.
            </p>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">Recuperado pelo comercial</p>
                <p className="mt-0.5 text-3xl font-semibold tracking-tight text-ink">{formatBRLFromCents(comercial.amountCents)}</p>
                <p className="mt-1 text-sm text-ink-muted">
                  {formatCount(comercial.count)} {plural(comercial.count, 'venda', 'vendas')} · comissão{' '}
                  <strong className="font-semibold text-ink">{formatBRLFromCents(comercial.commissionCents)}</strong>
                  {peso === null ? null : <> · {formatPercentFromBps(peso)} da comissão do recorte</>}
                </p>
                <Delta atual={comercial.amountCents} anterior={previous === null ? null : previous.comercialAmountCents} tipo="dinheiro" className="mt-1" />
              </div>
            </div>

            <div className="grid gap-2 sm:grid-cols-2">
              {(['comercial_webhook', 'venda_assistida'] as const).map((filtro) => {
                const via = filtro === 'comercial_webhook' ? 'webhook' : 'venda_assistida';
                const balde = via === 'webhook' ? comercial.viaWebhook : comercial.assisted;
                const info = ENTRADA[via];
                const ativo = filtroAtivo === filtro;
                return (
                  <button
                    key={filtro}
                    type="button"
                    aria-pressed={ativo}
                    onClick={() => onVerNoExtrato(filtro)}
                    title={info.hint}
                    className={`flex items-start justify-between gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors ${ativo ? 'border-brand bg-brand/5' : 'border-line bg-surface hover:border-brand/40'}`}
                  >
                    <span className="min-w-0">
                      <Badge tone={info.tone} icon={info.icon}>
                        {info.label}
                      </Badge>
                      <span className="mt-1.5 block text-sm text-ink">
                        <strong className="font-semibold tabular-nums">{formatCount(balde.count)}</strong> {plural(balde.count, 'venda', 'vendas')} ·{' '}
                        <span className="tabular-nums">{formatBRLFromCents(balde.recoveredAmountCents)}</span>
                      </span>
                      <span className="block text-xs text-ink-subtle">comissão {formatBRLFromCents(balde.commissionCents)}</span>
                    </span>
                    <span className="shrink-0 text-[11px] font-medium text-brand">{ativo ? 'Filtrando' : 'Ver'}</span>
                  </button>
                );
              })}
            </div>

            <div className="flex flex-col gap-2">
              <h3 className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtle">Quem mais fechou</h3>
              <ol className="flex flex-col divide-y divide-line/60">
                {vendedoras.map((v, i) => {
                  const empresa = identidadeDaEmpresa(v.companyId, companies, v.companyName);
                  return (
                    <li key={v.sellerId} className="flex flex-wrap items-center gap-3 py-2">
                      <span className="w-4 shrink-0 text-right text-xs tabular-nums text-ink-subtle">
                        {i + 1}
                        <span className="sr-only">º lugar:</span>
                      </span>
                      <CompanyLogo company={empresa} size="sm" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-ink">{shortenName(v.sellerName)}</p>
                        <p className="truncate text-xs text-ink-subtle">
                          {empresa.name} · {formatCount(v.count)} {plural(v.count, 'venda', 'vendas')}
                          {v.assistedCount > 0 ? ` · ${formatCount(v.assistedCount)} assistida${v.assistedCount === 1 ? '' : 's'}` : ''}
                        </p>
                      </div>
                      <span className="text-right">
                        <span className="block text-sm font-semibold tabular-nums text-ink">{formatBRLFromCents(v.commissionCents)}</span>
                        <span className="block text-[11px] text-ink-subtle">de comissão</span>
                      </span>
                      <Button variant="ghost" size="sm" icon="externo" onClick={() => onAbrirComercial(v.companyId)} aria-label={`Abrir o Comercial de ${empresa.name}`}>
                        Comercial
                      </Button>
                    </li>
                  );
                })}
              </ol>
            </div>
          </>
        )}

        <Footnote>
          Venda sem webhook (Pix manual, link de outra plataforma) entra como venda assistida pelo mesmo caminho de atribuição: 7 dias depois do toque, crédito ao
          último toque, valor pago.
        </Footnote>
      </div>
    </SectionCard>
  );
}
